import React, { useState, useContext, useEffect } from 'react';
import { StoryWrapper } from '../StoryWrapper';
import styles from '../week-reflection.module.css';
import { WeekReflectionContext } from '../WeekReflectionContext';
import { CSSTransition } from 'react-transition-group';
import SweetSavorerRewardImg from "../../../images/rewards/SweetSavorer.svg";
import EmotionalHonestyRewardImg from "../../../images/rewards/EmotionalHonesty.svg";
import ContextMasterRewardImg from "../../../images/rewards/ContextMaster.svg";
import DedicatedRewardImg from "../../../images/rewards/Dedicated.svg";

export const RewardsTally = ({ story, action, isPaused, config, storyData }) => {
    const rewardToImageMap = {
        SweetSavorer: SweetSavorerRewardImg,
        EmotionalHonesty: EmotionalHonestyRewardImg,
        ContextMaster: ContextMasterRewardImg,
        Dedicated: DedicatedRewardImg
    };

    const { animateRewards, setAnimateRewards } = useContext(WeekReflectionContext);
    const [showHeading, setShowHeading] = useState(!animateRewards);
    const [currentIndex, setCurrentIndex] = useState(animateRewards ? -1 : storyData.data.rewards.length);
    const [showImages, setShowImages] = useState(!animateRewards);

    useEffect(() => {
        if (!animateRewards) {
            return;
        }

        if (currentIndex === -1) {
            setTimeout(() => setShowHeading(true), 50);
            setTimeout(() => setCurrentIndex(0), 1300);
        }
        else if (currentIndex < storyData.data.rewards.length - 1) {
            const animationDelay = currentIndex === 1 && storyData.data.rewards[currentIndex][0] === 'Dedicated'
                ? 1500 // shorter coz dedication phrase is short
                : 2200;
            const timer = setTimeout(() => {
                setCurrentIndex(prevIndex => prevIndex + 1);
            }, animationDelay);
            return () => clearTimeout(timer);
        }
        else if (!showImages) {
            setTimeout(() => setShowImages(true), 1000);
        }
    }, [currentIndex, storyData.data.rewards.length, showImages]);

    const fadeIn = "opacity 1s ease-in-out, transform 1s ease-in-out";
    const transitionIn = "translateY(-15px)";

    return (
        <StoryWrapper storyName={storyData.component} action={action} isPaused={isPaused}>
            <CSSTransition timeout={1000} in={showHeading} appear={true}>
                <h1 className={`${styles.size2} ${styles.rewardsStoryHeading}`}
                    style={{
                        opacity: showHeading ? 1 : 0,
                        transform: showHeading ? "" : transitionIn,
                        transition: fadeIn
                    }}
                >
                    {storyData.data.heading}
                </h1>
            </ CSSTransition>

            {/* Display text elements one at a time */}
            {storyData.data.rewards.map((element, index) => (
                <CSSTransition key={index} timeout={1000} in={index <= currentIndex} appear={true}>
                    <div
                        style={{
                            opacity: index <= currentIndex ? 1 : 0,
                            transform: index <= currentIndex ? "" : transitionIn,
                            transition: fadeIn,
                            maxWidth: "18rem"
                        }}
                    >
                        <p style={{ margin: "1rem" }}>
                            {getRewardPhrase(
                                element[0],
                                element[1],
                                index,
                                storyData.data.rewards.length - 1,
                                storyData.data.heading,
                            )}
                        </p>
                        {index !== storyData.data.length - 1 && (
                            <hr className={styles.thinDivider} style={{ width: "100%", margin: 0 }} />
                        )}
                    </div>
                </CSSTransition>
            ))}

            {/* Show images all at once after text animation completes */}
            <CSSTransition timeout={1000} in={showImages} appear={true} onEntered={() => setAnimateRewards(false)}>
                <div
                    className={`${styles.flexWrapContainer} ${styles.rewardsStoryImages}`}
                    style={{
                        opacity: showImages ? 1 : 0,
                        transform: showImages ? "" : transitionIn,
                        transition: fadeIn,
                    }}
                >
                    {storyData.data.rewards.map(reward => (
                        <img
                            key={reward[0]}
                            src={rewardToImageMap[reward[0]]}
                            width={40}
                            style={{ margin: "0 0.25rem" }}
                        />
                    ))}
                </div>
            </CSSTransition>
        </StoryWrapper>
    );
};

const getRewardPhrase = (reward, count, currentIndex, maxIndex, heading) => {
    const isOne = count === 1;
    const hasHeading = heading?.length > 0;

    const phrases = {
        SweetSavorer: `Savored ${isOne ? "a" : count} sweet moment${isOne ? "" : "s"} at work${count < 3 ? "" : "!"}`,
        EmotionalHonesty: `Transformed ${isOne ? "a" : count} hard feeling${isOne ? "" : "s"} into insight`,
        ContextMaster: isOne ? "Left a trail of context for your future self once" : `Cultivated clarity by sharing context ${count} times`,
        Dedicated: 'Showed up with dedication'
    };

    if (currentIndex === 0 && !hasHeading) {
        return `You ${phrases[reward].toLowerCase()}`;
    }
    else {
        return (currentIndex < maxIndex) ? phrases[reward] : `And ${phrases[reward].toLowerCase()}`;
    }
};